<script setup lang="ts">import { computed as _computed } from 'vue';

import {
 AssetSummaryDto, AssetSummaryDtoV2, type MonitorSummaryDto,
} from '@/api';
import { getProviderIconSrc } from '@/utils/provider-icons';
import QualityStatus from '@/components/quality-status/Quality-Status.vue';
import SIcon from '@/components/SIcon.vue';
import SEllipsisTooltip from '@/components/SEllipsisTooltip/SEllipsisTooltip.vue';

interface AssetPreviewCardProps {
  entity: AssetSummaryDto | MonitorSummaryDto | AssetSummaryDtoV2;
}
const props = defineProps({
  entity: null
});

const icon = _computed(() => {
  switch (props.entity.displayedType) {
    case AssetSummaryDto.displayedType.DASHBOARD:
    case AssetSummaryDto.displayedType.REPORT:
    case AssetSummaryDto.displayedType.ANALYSIS:
    case AssetSummaryDto.displayedType.APP:
      return 'icon-chart-multiple';
    case AssetSummaryDto.displayedType.MODEL:
    case AssetSummaryDto.displayedType.DAG:
    case AssetSummaryDto.displayedType.ORCHESTRATOR:
    case AssetSummaryDto.displayedType.PIPELINE:
    case AssetSummaryDto.displayedType.CONNECTOR:
      return 'icon-pipeline-orchestrator';
    case AssetSummaryDto.displayedType.VIEW:
    case AssetSummaryDto.displayedType.MATERIALIZED_VIEW:
    case AssetSummaryDto.displayedType.VIEW_REF:
      return 'icon-data-view';
    case AssetSummaryDto.displayedType.DYNAMIC_TABLE:
    case AssetSummaryDto.displayedType.TABLE:
    case AssetSummaryDto.displayedType.SNOWFLAKE_STREAM:
    case AssetSummaryDto.displayedType.DATASET_REF:
    case AssetSummaryDto.displayedType.TABLE_REF:
    case AssetSummaryDto.displayedType.DATASET:
    case AssetSummaryDto.displayedType.NODE:
    case AssetSummaryDto.displayedType.STREAMING_TABLE:
    case AssetSummaryDto.displayedType.DROPPED_TABLE:
    case AssetSummaryDto.displayedType.TEMP_TABLE:
    case AssetSummaryDto.displayedType.EPHEMERAL_REF:
      return 'icon-data-table';
    case AssetSummaryDto.displayedType.EXTERNAL_TABLE:
      return 'icon-data-table-external';
    case AssetSummaryDto.displayedType.ML_MODEL:
      return 'icon-brain-circuit';
    default:
      return 'icon-shapes';
  }
});
const source = _computed(() => props.entity.lineagePlatformLabel);
const type = _computed(() => {
  if (props.entity.displayedType === AssetSummaryDto.displayedType.NONE) {
    return props.entity.displayedTypeLabel;
  }
  return props.entity.displayedType;
});
const imgsrc = _computed(() => (props.entity.lineagePlatform ? getProviderIconSrc(props.entity.lineagePlatform) : ''));
const alt = _computed(() => props.entity.lineagePlatform || '');
const name = _computed(() => props.entity.name || 'Unnamed Asset');
const hasHealthStatus = _computed(() => !!('healthStatus' in props.entity));
const healthStatus = _computed(() => {
  const entity = props.entity as AssetSummaryDtoV2;
  return entity.healthStatus;
});
const missingSince = _computed(() => {
  const entity = props.entity as AssetSummaryDtoV2;
  return entity.missingSince;
});
const scheduledDeletionDate = _computed(() => {
  const entity = props.entity as AssetSummaryDtoV2;
  return entity.scheduledDeletionDate;
});
const hasDeclaredSource = _computed(() => !!('declaredSource' in props.entity));
const isDeclaredSource = _computed(() => {
  if (!hasDeclaredSource.value) {
    return false;
  }
  const entity = props.entity as AssetSummaryDtoV2;
  return entity.declaredSource;
});
const hasSourceName = _computed(() => !!('sourceName' in props.entity));
const sourceName = _computed(() => {
  if (!hasSourceName.value) {
    return '';
  }
  const entity = props.entity as AssetSummaryDtoV2;
  return entity.sourceName;
});
const hasAssetType = _computed(() => !!('assetType' in props.entity));
const isDeclaredAsset = _computed(() => {
  if (!hasAssetType.value) {
    return false;
  }
  const entity = props.entity as AssetSummaryDtoV2;
  return entity.displayedType === AssetSummaryDto.displayedType.DECLARED_ASSET;
});

</script>

<template lang="pug">
v-row.asset-preview-card.pa-1( no-gutters )

  v-col.mr-2( cols="auto" )
    v-avatar(size="36" color="bgSecondary" :alt="alt")
      v-icon( color="iconNeutral" ) {{ icon }}

  v-col(
    cols="1"
    style="min-width: 100px; max-width: 100%;"
    class="flex-grow-1 flex-shrink-0" )

    .d-flex.mb-1.align-center
      v-avatar.mr-1(tile size="16")
        img( :src="imgsrc" :alt="alt")
      span.overline.grey--text.d-block
        | {{ source }}
        | {{ type }}

    .text-h6.font-weight-medium.mt-n1.break-spaces {{ name }}

    .mt-2
     .d-flex.align-center.flex-wrap
        QualityStatus.mr-4(
          v-if="hasHealthStatus && !isDeclaredAsset"
          :value="healthStatus"
          :missing-since="missingSince"
          :scheduled-deletion-date="scheduledDeletionDate"
        )
        .d-flex.align-center.flex-start
          .d-flex.align-center(v-if="isDeclaredAsset" data-track="declared-asset-hint")
            SIcon( icon="icon-arrow-right-circle-fill" size="small" color="iconNeutral" ).mr-1
            .caption.grey--text {{ $tc('asset_types.DECLARED_ASSET', 1) }}
          v-tooltip( top )
            template( v-slot:activator='{ on }' )
              .d-flex.align-center( v-on="on" )
                SIcon( v-if="hasDeclaredSource && isDeclaredSource" icon="icon-data-source-declarative" size="small" color="iconNeutral" data-track="declared-asset-icon").mr-1
                SIcon( v-else-if="hasSourceName" icon="icon-data-source" size="small" color="iconNeutral" ).mr-1
                SEllipsisTooltip(:content="sourceName" class="caption grey--text" style="max-width: 300px;")
            span {{ sourceName }}

</template>
